import type { Scenario } from "@radar-sim/schema";
import { Button, Card, Heading, Pill, Stack } from "../ui/primitives.js";

const DEMO_SCENARIO_IDS = [
  "g34-002-familiarisation-range-scales",
  "g34-006-cpa-overtaking",
];

export interface HomeProps {
  scenarios: ReadonlyArray<Scenario>;
  onLaunchDemo: (scenarioId: string) => void;
  onEnterApp: () => void;
}

export function Home(props: HomeProps): JSX.Element {
  const demoScenarios = props.scenarios.filter((s) =>
    DEMO_SCENARIO_IDS.includes(s.metadata.id),
  );

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1080,
        margin: "0 auto",
        padding: "32px 24px 80px",
        display: "flex",
        flexDirection: "column",
        gap: 64,
      }}
    >
      <Hero
        onEnterApp={props.onEnterApp}
        onLaunchDemo={() => {
          const first = demoScenarios[0];
          if (first !== undefined) props.onLaunchDemo(first.metadata.id);
        }}
        hasDemo={demoScenarios.length > 0}
      />
      <Demos scenarios={demoScenarios} onLaunchDemo={props.onLaunchDemo} />
      <FeatureGrid />
    </div>
  );
}

function Hero(props: {
  onEnterApp: () => void;
  onLaunchDemo: () => void;
  hasDemo: boolean;
}): JSX.Element {
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr",
        gap: 48,
        alignItems: "center",
      }}
    >
      <Stack gap={20}>
        <Stack direction="row" gap={8} wrap align="center">
          <Pill tone="accent">RYA G34 Radar Course</Pill>
          <a
            href="https://goastern.com/"
            style={{
              fontSize: 13,
              color: "var(--text-muted)",
              textDecoration: "none",
            }}
          >
            from <strong style={{ color: "var(--accent)" }}>GoAstern</strong> ↗
          </a>
        </Stack>
        <Heading level={1} style={{ fontSize: 44, lineHeight: 1.1 }}>
          Marine radar training,{" "}
          <span style={{ color: "var(--accent)" }}>on every screen</span>.
        </Heading>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.55,
            color: "var(--text-muted)",
            margin: 0,
            maxWidth: 560,
          }}
        >
          A faithful Plan-Position Indicator with MARPA, EBL/VRM, sea
          clutter, rain masking, and twenty G34-aligned scenarios across
          the Solent and Weymouth/Portland — running on the same engine
          in solo, classroom, and assessment-grade modes. Built by{" "}
          <a href="https://goastern.com/">GoAstern</a>, the passage-planning
          app for cruising sailors.
        </p>
        <Stack direction="row" gap={12} wrap>
          {props.hasDemo && (
            <Button size="lg" onClick={props.onLaunchDemo}>
              Try a demo →
            </Button>
          )}
          <Button size="lg" variant="secondary" onClick={props.onEnterApp}>
            Open simulator
          </Button>
        </Stack>
      </Stack>
      <HeroVisual />
    </section>
  );
}

/**
 * Schematic radar PPI rendered with SVG — no canvas, no engine, just a
 * decorative element next to the hero text. Hints at the product
 * without replacing the real PPI further down the funnel.
 */
function HeroVisual(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        aspectRatio: "1 / 1",
        width: "100%",
        maxWidth: 380,
        marginLeft: "auto",
      }}
    >
      <svg
        viewBox="0 0 200 200"
        style={{
          width: "100%",
          height: "100%",
          filter: "drop-shadow(0 16px 40px rgba(56,189,248,0.25))",
        }}
      >
        <defs>
          <radialGradient id="ppiBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0c1f1a" />
            <stop offset="100%" stopColor="#020a08" />
          </radialGradient>
          <linearGradient id="sweep" x1="50%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="rgba(56,189,248,0.5)" />
            <stop offset="100%" stopColor="rgba(56,189,248,0)" />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="92" fill="url(#ppiBg)" />
        {[25, 50, 75, 92].map((r) => (
          <circle
            key={r}
            cx="100"
            cy="100"
            r={r}
            fill="none"
            stroke="rgba(56,189,248,0.18)"
            strokeWidth="0.6"
          />
        ))}
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
          const rad = ((deg - 90) * Math.PI) / 180;
          return (
            <line
              key={deg}
              x1="100"
              y1="100"
              x2={100 + 92 * Math.cos(rad)}
              y2={100 + 92 * Math.sin(rad)}
              stroke="rgba(56,189,248,0.08)"
              strokeWidth="0.5"
            />
          );
        })}
        <line
          x1="100"
          y1="100"
          x2="100"
          y2="8"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="1"
        />
        <g style={{ transformOrigin: "100px 100px" }}>
          <path
            d="M 100 100 L 192 100 A 92 92 0 0 0 178.7 47.3 Z"
            fill="url(#sweep)"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 100 100"
              to="360 100 100"
              dur="4s"
              repeatCount="indefinite"
            />
          </path>
        </g>
        <circle cx="100" cy="100" r="3" fill="#f3f4f6" />
        {[
          { x: 152, y: 64 },
          { x: 70, y: 130 },
          { x: 130, y: 145 },
        ].map((t, i) => (
          <g key={i}>
            <circle cx={t.x} cy={t.y} r="3" fill="#a7f3d0" />
            <circle
              cx={t.x}
              cy={t.y}
              r="6"
              fill="none"
              stroke="rgba(167,243,208,0.4)"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

function Demos(props: {
  scenarios: ReadonlyArray<Scenario>;
  onLaunchDemo: (id: string) => void;
}): JSX.Element {
  return (
    <section id="demos" aria-labelledby="demos-heading">
      <Stack gap={20}>
        <Stack gap={6}>
          <Pill tone="success">No account required</Pill>
          <Heading level={2} style={{ fontSize: 28 }}>
            Run a free demo right now
          </Heading>
          <p style={{ color: "var(--text-muted)", margin: 0, fontSize: 15 }}>
            Two scenarios with full radar fidelity, in your browser, without
            signing in.
          </p>
        </Stack>
        {props.scenarios.length === 0 ? (
          <Card>
            <p style={{ margin: 0, color: "var(--text-muted)" }}>
              No demo scenarios are bundled in this build.
            </p>
          </Card>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 16,
            }}
          >
            {props.scenarios.map((s) => (
              <DemoCard
                key={s.metadata.id}
                scenario={s}
                onLaunch={() => props.onLaunchDemo(s.metadata.id)}
              />
            ))}
          </div>
        )}
      </Stack>
    </section>
  );
}

function DemoCard(props: {
  scenario: Scenario;
  onLaunch: () => void;
}): JSX.Element {
  const s = props.scenario;
  return (
    <Card padding={20}>
      <Stack gap={12} style={{ height: "100%" }}>
        <Stack direction="row" gap={8} wrap align="center">
          <Pill>{s.metadata.area}</Pill>
          <Pill>Difficulty {s.metadata.difficulty}/5</Pill>
          <Pill>{s.metadata.expectedDurationMinutes} min</Pill>
        </Stack>
        <Heading level={3} style={{ fontSize: 17 }}>
          {s.metadata.name}
        </Heading>
        <p
          style={{
            color: "var(--text-muted)",
            margin: 0,
            fontSize: 14,
            lineHeight: 1.5,
            flex: 1,
          }}
        >
          {s.metadata.description}
        </p>
        <Button onClick={props.onLaunch} fullWidth>
          Launch demo
        </Button>
      </Stack>
    </Card>
  );
}

function FeatureGrid(): JSX.Element {
  const features: { title: string; body: string }[] = [
    {
      title: "Twenty G34-aligned scenarios",
      body: "Familiarisation, target identification, collision avoidance, restricted visibility, MOB recovery, and assessment-grade composites — every G34 syllabus item covered.",
    },
    {
      title: "Solo, classroom, assessment",
      body: "One simulation engine, three runtimes. Practise alone, run a synchronised classroom of tablets, or grade a six-student assessment from a multi-tile dashboard.",
    },
    {
      title: "Authoring + plotting",
      body: "Live-validating JSON editor with templates for every vessel and event kind. Plotting sheet with automatic CPA / TCPA from any two fixes. OAW velocity-triangle calculator for sail teaching.",
    },
    {
      title: "Honest physics",
      body: "Sea clutter scaled by sea state and relative wind. Rain cells genuinely mask returns behind them. Gain / STC / FTC / tune controls bite as on a real device.",
    },
    {
      title: "Replay-mode debrief",
      body: "Every classroom and assessment session captures snapshot recordings. Student and instructor watch identical playback through the same chrome and PPI used live.",
    },
    {
      title: "Two device chromes",
      body: "Garmin GPSMAP and Raymarine Axiom pastiches, so students practise on the device family they're likely to meet at sea — same engine inside.",
    },
  ];

  return (
    <section aria-labelledby="features-heading">
      <Stack gap={24}>
        <Heading level={2} style={{ fontSize: 28 }} id="features-heading">
          Built for honest training
        </Heading>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {features.map((f) => (
            <Card key={f.title}>
              <Heading
                level={3}
                style={{ fontSize: 15, marginBottom: 8 }}
              >
                {f.title}
              </Heading>
              <p
                style={{
                  margin: 0,
                  color: "var(--text-muted)",
                  fontSize: 14,
                  lineHeight: 1.55,
                }}
              >
                {f.body}
              </p>
            </Card>
          ))}
        </div>
      </Stack>
    </section>
  );
}

